import { type ReactNode, useId } from "react";

import classNames from "classnames";

import style from "./PricingBillingPeriodPicker.module.css";

export type PricingBillingPeriodPickerProps = {
  /** When true, the toggle is in the “yearly” position. */
  isYearly: boolean;
  onYearlyChange: (isYearly: boolean) => void;
  monthlyLabel: ReactNode;
  yearlyLabel: ReactNode;
  /** Optional content after the yearly label (e.g. savings callout). */
  yearlyExtra?: ReactNode;
  className?: string;
};

/**
 * Monthly / yearly billing toggle used on marketing pricing surfaces.
 */
export function PricingBillingPeriodPicker({
  isYearly,
  onYearlyChange,
  monthlyLabel,
  yearlyLabel,
  yearlyExtra,
  className,
}: PricingBillingPeriodPickerProps) {
  const toggleId = useId();

  return (
    <div className={classNames(style.periodPicker, className)}>
      <button
        type="button"
        className={style.periodLabel}
        data-active={!isYearly || undefined}
        onClick={() => onYearlyChange(false)}
      >
        {monthlyLabel}
      </button>

      <div className={style.toggle}>
        <input
          id={toggleId}
          type="checkbox"
          className={style.toggleInput}
          role="switch"
          aria-checked={isYearly}
          aria-label="Billing period"
          checked={isYearly}
          onChange={(e) => onYearlyChange(e.target.checked)}
        />
        <label
          htmlFor={toggleId}
          className={style.toggleTrack}
          data-checked={isYearly || undefined}
        >
          <span className={style.toggleThumb} data-checked={isYearly || undefined} />
        </label>
      </div>

      <button
        type="button"
        className={style.periodLabel}
        data-active={isYearly || undefined}
        onClick={() => onYearlyChange(true)}
      >
        {yearlyLabel}
        {yearlyExtra ? <span className={style.yearlyExtra}>{yearlyExtra}</span> : null}
      </button>
    </div>
  );
}
